import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { Check, Plus } from 'lucide-react';
import { clsx } from '../clsx';
import { useAnchorPosition, useFieldIds, useOutsideClick } from '../hooks';
import { Caret } from './Caret';
import { FieldFrame } from './FieldFrame';
import { Icon } from './Icon';
import styles from './Combobox.module.css';

export interface ComboboxOption {
  id: string;
  label: string;
  /** Second line, 11.5 px subtle ("Käufer · Regensburg"). */
  description?: string | undefined;
  /** Options with the same group are listed under one eyebrow, in first-seen order. */
  group?: string | undefined;
  disabled?: boolean | undefined;
}

export interface ComboboxProps {
  label: string;
  labelHidden?: boolean | undefined;
  options: ReadonlyArray<ComboboxOption>;
  /** Id of the selected option. */
  value: string | null;
  onChange: (id: string | null, option: ComboboxOption | null) => void;
  /** Adds the last row "„<Eingabe>“ neu anlegen" when the query matches no option exactly. */
  onCreate?: ((query: string) => void) | undefined;
  createLabel?: ((query: string) => string) | undefined;
  /** Called on every keystroke (server-side search); the client still filters unless `filter` is false. */
  onQueryChange?: ((query: string) => void) | undefined;
  filter?: boolean | undefined;
  emptyText?: string | undefined;
  placeholder?: string | undefined;
  hint?: string | undefined;
  error?: string | undefined;
  invalid?: boolean | undefined;
  required?: boolean | undefined;
  optional?: boolean | undefined;
  leading?: ReactNode | undefined;
  disabled?: boolean | undefined;
  readOnly?: boolean | undefined;
  id?: string | undefined;
  name?: string | undefined;
  className?: string | undefined;
  /** Focus the input on mount. */
  autoFocus?: boolean | undefined;
}

type Row = { kind: 'option'; option: ComboboxOption } | { kind: 'create' };

function highlight(text: string, query: string): ReactNode {
  if (!query) return text;
  const index = text.toLocaleLowerCase('de').indexOf(query.toLocaleLowerCase('de'));
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark className={styles.mark}>{text.slice(index, index + query.length)}</mark>
      {text.slice(index + query.length)}
    </>
  );
}

/** Input + listbox with keyboard navigation, groups and a "create new" row (contact pickers, event type search). */
export function Combobox({
  label,
  labelHidden,
  options,
  value,
  onChange,
  onCreate,
  createLabel = (query) => `„${query}“ neu anlegen`,
  onQueryChange,
  filter = true,
  emptyText = 'Keine Treffer',
  placeholder,
  hint,
  error,
  invalid,
  required,
  optional,
  leading,
  disabled,
  readOnly,
  id,
  name,
  className,
  autoFocus,
}: ComboboxProps) {
  const ids = useFieldIds(id, { hint: Boolean(hint), error: Boolean(error) });
  const listboxId = `${ids.inputId}-listbox`;
  const controlRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const selected = useMemo(() => options.find((option) => option.id === value) ?? null, [options, value]);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState(selected?.label ?? '');
  const [active, setActive] = useState(0);
  const [width, setWidth] = useState<number>();
  const position = useAnchorPosition(controlRef, listRef, open, 'bottom-start', 4);

  useEffect(() => {
    if (!open) setQuery(selected?.label ?? '');
  }, [selected, open]);

  const rows = useMemo<Row[]>(() => {
    const needle = query.trim().toLocaleLowerCase('de');
    const visible =
      filter && needle && query !== selected?.label
        ? options.filter(
            (option) =>
              option.label.toLocaleLowerCase('de').includes(needle) ||
              (option.description ?? '').toLocaleLowerCase('de').includes(needle),
          )
        : options;
    const list: Row[] = visible.map((option) => ({ kind: 'option', option }));
    const exact = options.some((option) => option.label.toLocaleLowerCase('de') === needle);
    if (onCreate && needle && !exact) list.push({ kind: 'create' });
    return list;
  }, [filter, onCreate, options, query, selected]);

  const groups = useMemo(() => {
    const order: Array<{ group: string | undefined; rows: Array<{ row: Row; index: number }> }> = [];
    rows.forEach((row, index) => {
      const group = row.kind === 'option' ? row.option.group : undefined;
      let bucket = order.find((entry) => entry.group === group);
      if (!bucket) {
        bucket = { group, rows: [] };
        order.push(bucket);
      }
      bucket.rows.push({ row, index });
    });
    return order;
  }, [rows]);

  useLayoutEffect(() => {
    if (!open) return;
    setActive(0);
    setWidth(controlRef.current?.getBoundingClientRect().width);
  }, [open, rows.length]);

  useEffect(() => {
    if (!open) return;
    const element = listRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`);
    element?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  useOutsideClick([controlRef, listRef], () => setOpen(false), open);

  function commit(row: Row | undefined) {
    if (!row) return;
    if (row.kind === 'create') {
      onCreate?.(query.trim());
      setOpen(false);
      return;
    }
    if (row.option.disabled) return;
    onChange(row.option.id, row.option);
    setQuery(row.option.label);
    setOpen(false);
  }

  function clear() {
    onChange(null, null);
    setQuery('');
  }

  function onKeyDown(event: KeyboardEvent<HTMLInputElement>) {
    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (!open) setOpen(true);
        else setActive((index) => (rows.length ? (index + 1) % rows.length : 0));
        break;
      case 'ArrowUp':
        event.preventDefault();
        if (!open) setOpen(true);
        else setActive((index) => (rows.length ? (index - 1 + rows.length) % rows.length : 0));
        break;
      case 'Home':
        if (open) {
          event.preventDefault();
          setActive(0);
        }
        break;
      case 'End':
        if (open) {
          event.preventDefault();
          setActive(Math.max(0, rows.length - 1));
        }
        break;
      case 'Enter':
        if (open) {
          event.preventDefault();
          commit(rows[active]);
        }
        break;
      case 'Escape':
        if (open) {
          event.preventDefault();
          event.stopPropagation();
          setOpen(false);
          setQuery(selected?.label ?? '');
        }
        break;
      case 'Tab':
        setOpen(false);
        break;
      case 'Backspace':
        if (selected && query === selected.label) clear();
        break;
    }
  }

  const activeId = open && rows[active] ? `${listboxId}-${active}` : undefined;

  return (
    <>
      <FieldFrame
        htmlFor={ids.inputId}
        label={label}
        labelHidden={labelHidden}
        required={required}
        optional={optional}
        hint={hint}
        error={error}
        hintId={ids.hintId}
        errorId={ids.errorId}
        invalid={invalid}
        readOnly={readOnly}
        disabled={disabled}
        leading={leading}
        trailing={<Caret size={11} />}
        controlRef={controlRef}
        className={className}
      >
        <input
          ref={inputRef}
          id={ids.inputId}
          name={name}
          type="text"
          role="combobox"
          autoComplete="off"
          autoFocus={autoFocus}
          aria-expanded={open}
          aria-controls={listboxId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          aria-invalid={error || invalid ? true : undefined}
          aria-describedby={ids.describedBy}
          aria-required={required || undefined}
          className={styles.input}
          placeholder={placeholder}
          value={query}
          disabled={disabled}
          readOnly={readOnly}
          onChange={(event) => {
            setQuery(event.target.value);
            onQueryChange?.(event.target.value);
            if (!open) setOpen(true);
            if (selected && event.target.value !== selected.label) onChange(null, null);
          }}
          onFocus={() => {
            if (!readOnly && !disabled) setOpen(true);
          }}
          onClick={() => {
            if (!readOnly && !disabled) setOpen(true);
          }}
          onKeyDown={onKeyDown}
        />
      </FieldFrame>
      {open
        ? createPortal(
            <div
              ref={listRef}
              id={listboxId}
              role="listbox"
              aria-label={label}
              className={styles.listbox}
              style={{ ...position.style, width }}
            >
              {rows.length === 0 ? <div className={styles.empty}>{emptyText}</div> : null}
              {groups.map((bucket, bucketIndex) => (
                <div
                  key={bucket.group ?? `group-${bucketIndex}`}
                  role={bucket.group ? 'group' : undefined}
                  aria-labelledby={bucket.group ? `${listboxId}-g${bucketIndex}` : undefined}
                  className={styles.group}
                >
                  {bucket.group ? (
                    <div id={`${listboxId}-g${bucketIndex}`} className={styles.groupLabel}>
                      {bucket.group}
                    </div>
                  ) : null}
                  {bucket.rows.map(({ row, index }) =>
                    row.kind === 'create' ? (
                      <div
                        key="create"
                        id={`${listboxId}-${index}`}
                        role="option"
                        aria-selected={index === active}
                        data-index={index}
                        className={clsx(styles.option, styles.create, index === active && styles.active)}
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => commit(row)}
                      >
                        <Icon icon={Plus} size={16} />
                        <span className={styles.optionLabel}>{createLabel(query.trim())}</span>
                      </div>
                    ) : (
                      <div
                        key={row.option.id}
                        id={`${listboxId}-${index}`}
                        role="option"
                        aria-selected={row.option.id === value}
                        aria-disabled={row.option.disabled || undefined}
                        data-index={index}
                        className={clsx(
                          styles.option,
                          index === active && styles.active,
                          row.option.disabled && styles.disabled,
                        )}
                        onMouseDown={(event) => event.preventDefault()}
                        onMouseEnter={() => setActive(index)}
                        onClick={() => commit(row)}
                      >
                        <span className={styles.optionText}>
                          <span className={styles.optionLabel}>
                            {highlight(row.option.label, query.trim())}
                          </span>
                          {row.option.description ? (
                            <span className={styles.optionDescription}>{row.option.description}</span>
                          ) : null}
                        </span>
                        {row.option.id === value ? (
                          <Icon icon={Check} size={16} className={styles.check} />
                        ) : null}
                      </div>
                    ),
                  )}
                </div>
              ))}
            </div>,
            document.body,
          )
        : null}
    </>
  );
}
